import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import http from "../api/http";
import CommunicationMethod from "../types/communicationMethod";
import SearchParams, { defaultParams } from "../types/searchParams";
import SearchResults from "../types/searchResults";
import getSearchQueryString from "../helpers/getSearchQueryString";

interface CommunicationMethodState {
  communicationMethod: CommunicationMethod;
  communicationMethodFilter: SearchParams;
  communicationMethodSearchResults?: SearchResults;
  status: "idle" | "loading" | "failed";
}

const initialState: CommunicationMethodState = {
  communicationMethod: { id: 0, name: "" },
  communicationMethodFilter: defaultParams,
  status: "idle",
};

export const getCommunicationMethodId = createAsyncThunk<
  CommunicationMethod,
  number
>("communication-method/get/id", async (id: number) => {
  const response = await http.get<CommunicationMethod>(
    `/communicationmethod/${id}`
  );
  return response.data;
});

export const editCommunicationMethod = createAsyncThunk<
  CommunicationMethod,
  CommunicationMethod
>("communication-method/edit", async (method: CommunicationMethod) => {
  const response = await http.put<CommunicationMethod>(
    `/communicationmethod/${method.id}`,
    method,
    { params: { successToast: true } }
  );
  return response.data;
});

export const deleteCommunicationMethod = createAsyncThunk<
  CommunicationMethod,
  number
>("communication-method/delete", async (id: number) => {
  const response = await http.delete<CommunicationMethod>(
    `/communicationmethod/${id}`,
    { params: { successToast: true } }
  );
  return response.data;
});

export const postCommunicationMethod = createAsyncThunk<
  CommunicationMethod,
  CommunicationMethod
>("communication-method/post", async (method: CommunicationMethod) => {
  const response = await http.post<CommunicationMethod>(
    `/communicationmethod`,
    method,
    { params: { successToast: true } }
  );
  return response.data;
});

export const searchCommunicationMethod = createAsyncThunk<
  SearchResults,
  SearchParams
>("communication-method/search", async (search: SearchParams) => {
  let qs = getSearchQueryString(search);
  const response = await http.get<SearchResults>(
    `/communicationmethod/search?${qs}`
  );
  return response.data;
});

const communicationMethodSlice = createSlice({
  name: "communicationMethod",
  initialState,
  reducers: {
    resetCommunicationMethod(state) {
      state.communicationMethod = { ...initialState.communicationMethod };
    },
    setCommunicationMethodFilter(state, action) {
      state.communicationMethod = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getCommunicationMethodId.pending, (state) => {
        state.status = "loading";
      })
      .addCase(getCommunicationMethodId.fulfilled, (state, action) => {
        state.status = "idle";
        state.communicationMethod = action.payload;
      })
      .addCase(getCommunicationMethodId.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(editCommunicationMethod.pending, (state) => {
        state.status = "loading";
      })
      .addCase(editCommunicationMethod.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(editCommunicationMethod.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(deleteCommunicationMethod.pending, (state) => {
        state.status = "loading";
      })
      .addCase(deleteCommunicationMethod.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(deleteCommunicationMethod.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(postCommunicationMethod.pending, (state) => {
        state.status = "loading";
      })
      .addCase(postCommunicationMethod.fulfilled, (state) => {
        state.status = "idle";
      })
      .addCase(postCommunicationMethod.rejected, (state) => {
        state.status = "failed";
      })
      .addCase(searchCommunicationMethod.pending, (state) => {
        state.status = "loading";
      })
      .addCase(searchCommunicationMethod.fulfilled, (state, action) => {
        state.status = "idle";
        state.communicationMethodSearchResults = action.payload;
      })
      .addCase(searchCommunicationMethod.rejected, (state) => {
        state.status = "failed";
      });
  },
});

export default communicationMethodSlice.reducer;
export const { resetCommunicationMethod, setCommunicationMethodFilter } =
  communicationMethodSlice.actions;
